import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Select } from '@tempo-io/sui-select';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { Team } from '@/lib/api/types/teams';
import { buildGroupedTeamOptions } from '@/lib/utils/teams/buildGroupedTeamOptions';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';

interface TeamOption {
  value: number;
  label: string;
  icon: React.ReactNode | null;
}

const mapTeamToOption = (team: Team): TeamOption => ({
  value: team.id,
  label: team.name,
  icon: team.icon ? <img src={team.icon} alt={team.name} className="w-4 h-4 rounded-full" /> : null,
});

export const TeamEditCell = ({
  teamsById,
  loopId,
  id,
  row,
  field,
  api,
}: GridRenderEditCellParams<ItemsTableRow> & {
  teamsById: Record<Team['id'], Team>;
  loopId?: number;
}) => {
  const cellId = `team-assignment-${id}-${field}`;
  const { t } = useTranslation();
  const itemId = Number(id);

  const teamAssignmentOptions = React.useMemo(
    () =>
      buildGroupedTeamOptions(Object.values(teamsById), loopId, mapTeamToOption, {
        inPortfolio: t(($) => $.adaptivePlanner.teamDropdown.group.inPortfolio),
        notInPortfolio: t(($) => $.adaptivePlanner.teamDropdown.group.notInPortfolio),
      }),
    [teamsById, loopId, t],
  );

  const { commit, isPending } = useTableCellMutation();

  const selectedOption = React.useMemo(() => {
    if (!row.teamId) return null;
    const team = teamsById[row.teamId];
    return team ? mapTeamToOption(team) : null;
  }, [row.teamId, teamsById]);

  return (
    <Select<TeamOption>
      id={cellId}
      name={cellId}
      cellEdit
      autoFocus
      openMenuOnFocus
      menuPlacement="auto"
      isClearable
      value={selectedOption}
      onChange={(opt) => {
        commit({
          itemId,
          api,
          row,
          data: { teamId: opt ? opt.value : null },
        });
      }}
      options={teamAssignmentOptions}
      formatOptionLabel={(option) => <OptionWithIcon option={option} />}
      getOptionValue={(option) => String(option.value)}
      getOptionLabel={(option) => option.label}
      placeholder={t(($) => $.adaptivePlanner.itemPanel.selectPlaceholders.team)}
      isDisabled={isPending}
      closeMenuOnScroll={(e: Event) =>
        e.target instanceof HTMLElement && !e.target.closest('[class*="-menu"]')
      }
    />
  );
};

const OptionWithIcon = ({
  option,
}: {
  option: { label: string; icon?: React.ReactNode | null };
}) => (
  <div className="flex items-center gap-2 min-w-0">
    {option.icon}
    <span className="truncate">{option.label}</span>
  </div>
);
